import { Injectable, signal } from '@angular/core'
import { Router } from '@angular/router'
import { Tab, NavigationItem } from './models/tab.model'
import { RouteMetadataService } from './route-metadata.service'

/**
 * TabService manages tab state and lifecycle.
 *
 * This service provides both manual tab creation (legacy) and automatic
 * route-based tab creation (new global handler approach).
 *
 * Key Features:
 * - Automatic tab creation from route navigation
	 * - Tab limit enforcement (configurable, default: 6)
 * - Persistent tab storage in localStorage
 * - Route-based tab management
 *
 * Usage:
 * - Automatic: Use global route handler in app.component.ts
 * - Manual: Use createTab() method for legacy components
 */
@Injectable({
	providedIn: 'root'
})
export class TabService {
	private readonly STORAGE_KEY = 'app-tabs'
	private readonly DEFAULT_MAX_TABS = 6
	private maxTabs: number = this.DEFAULT_MAX_TABS

	private tabsSignal = signal<Tab[]>([])
	private activeTabIdSignal = signal<string | null>(null)
	private isUpdatingRoute = false // Prevent infinite loops

	readonly tabs = this.tabsSignal.asReadonly()
	readonly activeTabId = this.activeTabIdSignal.asReadonly()

	constructor(
		private router: Router,
		private routeMetadata: RouteMetadataService
	) {
		this.loadTabsFromStorage()
		this.initializeTabLimit()
	}

	/**
	 * Initialize tab limit from configuration or localStorage
	 */
	private initializeTabLimit(): void {
		const storedLimit = localStorage.getItem('app-max-tabs')
		if (storedLimit) {
			this.maxTabs = parseInt(storedLimit, 10) || this.DEFAULT_MAX_TABS
		}
	}

	createTab(navigationItem: NavigationItem): Tab {
		const existingTab = this.findTabByRoute(navigationItem.route)
		if (existingTab) {
			this.switchToTab(existingTab.id)
			return existingTab
		}

		// Check tab limit and auto-close oldest if needed
		if (this.tabsSignal().length >= this.maxTabs) {
			const closedTab = this.closeOldestTab()
			if (closedTab) {
				console.log(`TabService: Auto-closed oldest tab "${closedTab.title}" to make room for new tab`)
			}
		}

		const newTab: Tab = {
			id: this.generateTabId(),
			title: navigationItem.title,
			route: navigationItem.route,
			isActive: true,
			createdAt: Date.now()
		}

		const currentTabs = this.tabsSignal()
		const updatedTabs = currentTabs.map(tab => ({...tab, isActive: false}))
		this.tabsSignal.set([...updatedTabs, newTab])
		this.activeTabIdSignal.set(newTab.id)

		this.saveTabsToStorage()
		if (navigationItem.shouldNavigateToTab) {
			this.router.navigate([navigationItem.route])
		}

		return newTab
	}

	/**
	 * Activate a tab without triggering navigation
	 */
	activateTab(tabId: string): void {
		const currentTabs = this.tabsSignal()
		const currentUrl = this.router.url

		const updatedTabs = currentTabs.map(tab => {
			if (tab.id === tabId) {
				// Update tab route with current query parameters
				const updatedRoute = this.mergeQueryParams(tab.route, currentUrl)
				return { ...tab, isActive: true, route: updatedRoute }
			}
			return { ...tab, isActive: false }
		})

		const targetTab = updatedTabs.find(tab => tab.id === tabId)
		if (targetTab) {
			this.tabsSignal.set(updatedTabs)
			this.activeTabIdSignal.set(tabId)
			this.saveTabsToStorage()
		}
	}

	switchToTab(tabId: string): void {
		try {
			// console.log('TabService: switchToTab called', tabId)

			if (!tabId) {
				console.error('TabService: tabId is undefined or null')
				return
			}

			const currentTabs = this.tabsSignal()
			const currentUrl = this.router.url

		const updatedTabs = currentTabs.map(tab => {
			if (tab.id === tabId) {
				// Only update query parameters if tab doesn't already have them
				// or if current URL has different query parameters
				const tabHasQueryParams = this.extractQueryParams(tab.route) !== ''
				const currentHasQueryParams = this.extractQueryParams(currentUrl) !== ''

				let updatedRoute = tab.route
				if (!tabHasQueryParams || currentHasQueryParams) {
					updatedRoute = this.mergeQueryParams(tab.route, currentUrl)
				}

				return { ...tab, isActive: true, route: updatedRoute }
			}
			return { ...tab, isActive: false }
		})

		const targetTab = updatedTabs.find(tab => tab.id === tabId)
		if (targetTab) {
			this.tabsSignal.set(updatedTabs)
			this.activeTabIdSignal.set(tabId)

			// Only navigate if the target route is different from current URL
			const currentUrl = this.router.url
			const targetUrl = targetTab.route.startsWith('/') ? targetTab.route : '/' + targetTab.route

			if (currentUrl !== targetUrl) {
				// console.log('TabService: Navigating to', targetUrl)
				this.router.navigateByUrl(targetUrl)
			} else {
				// console.log('TabService: Already on target route, skipping navigation')
			}

			this.saveTabsToStorage()
		} else {
			console.error('TabService: Target tab not found', tabId)
		}
		} catch (error) {
			console.error('TabService: Error in switchToTab', error)
		}
	}

	closeTab(tabId: string): void {
		try {
			// console.log('TabService: closeTab called', tabId)

			if (!tabId) {
				console.error('TabService: tabId is undefined or null in closeTab')
				return
			}

			const currentTabs = this.tabsSignal()
			const tabToClose = currentTabs.find(tab => tab.id === tabId)

			if (!tabToClose) {
				console.error('TabService: Tab to close not found', tabId)
				return
			}

			let updatedTabs = currentTabs.filter(tab => tab.id !== tabId)

			if (tabToClose.isActive && updatedTabs.length > 0) {
				const tabIndex = currentTabs.findIndex(tab => tab.id === tabId)
				const nextTab = updatedTabs[Math.max(0, tabIndex - 1)]
				nextTab.isActive = true
				this.activeTabIdSignal.set(nextTab.id)
				const navigationUrl = nextTab.route.startsWith('/') ? nextTab.route : '/' + nextTab.route
				this.router.navigateByUrl(navigationUrl)
			}

			this.tabsSignal.set(updatedTabs)
			this.saveTabsToStorage()
		} catch (error) {
			console.error('TabService: Error in closeTab', error)
		}
	}

	private findTabByRoute(route: string): Tab | null {
		// Clean route to remove query parameters for comparison
		const cleanRoute = this.cleanRouteForComparison(route)
		return this.tabsSignal().find(tab => {
			const cleanTabRoute = this.cleanRouteForComparison(tab.route)
			return cleanTabRoute === cleanRoute
		}) || null
	}

	/**
	 * Clean route for comparison by removing query parameters and hash
	 */
	private cleanRouteForComparison(route: string): string {
		let cleanPath = route

		// Remove hash prefix if present
		if (cleanPath.startsWith('#/')) {
			cleanPath = cleanPath.substring(2)
		}

		// Remove query parameters
		cleanPath = cleanPath.split('?')[0]

		// Remove trailing slash for consistency
		if (cleanPath.endsWith('/')) {
			cleanPath = cleanPath.slice(0, -1)
		}

		return cleanPath
	}

	private generateTabId(): string {
		return `tab-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
	}

	private saveTabsToStorage(): void {
		const tabs = this.tabsSignal()
		localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tabs))
	}

	private loadTabsFromStorage(): void {
		try {
			const storedTabs = localStorage.getItem(this.STORAGE_KEY)
			if (storedTabs) {
				const tabs: Tab[] = JSON.parse(storedTabs)
				this.tabsSignal.set(tabs)

				const activeTab = tabs.find(tab => tab.isActive)
				if (activeTab) {
					this.activeTabIdSignal.set(activeTab.id)
				}
			}
		} catch (error) {
			console.warn('Failed to load tabs from storage:', error)
		}
	}

	/**
	 * Clear all tabs and reset state
	 */
	clearTabs(): void {
		this.tabsSignal.set([])
		this.activeTabIdSignal.set(null)
		this.saveTabsToStorage()
	}

	// NEW ROUTE-BASED METHODS

	/**
	 * Create a tab from the current route
	 */
	createTabFromRoute(route: string): Tab | null {
		// console.log('TabService: createTabFromRoute called with:', route)

		// Check if route should be excluded
		if (this.routeMetadata.isExcludedRoute(route)) {
			return null
		}

		// Check if tab already exists
		const existingTab = this.findTabByRoute(route)
		if (existingTab) {
			this.switchToTab(existingTab.id)
			return existingTab
		}

		// Check tab limit and auto-close oldest if needed
		if (this.tabsSignal().length >= this.maxTabs) {
			const closedTab = this.closeOldestTab()
			if (closedTab) {
				console.log(`TabService: Auto-closed oldest tab "${closedTab.title}" to make room for new tab`)
			}
		}

		// Get tab configuration and title
		const tabTitle = this.getTabTitleForRoute(route)
		// console.log('TabService: Generated title:', tabTitle, 'for route:', route)

		const newTab: Tab = {
			id: this.generateTabId(),
			title: tabTitle,
			route: route,
			isActive: true,
			createdAt: Date.now()
		}

		// console.log('TabService: Creating new tab:', newTab)

		const currentTabs = this.tabsSignal()
		const updatedTabs = currentTabs.map(tab => ({...tab, isActive: false}))
		this.tabsSignal.set([...updatedTabs, newTab])
		this.activeTabIdSignal.set(newTab.id)

		this.saveTabsToStorage()
		return newTab
	}

	/**
	 * Ensure a tab exists for given route
	 */
	syncTabWithRoute(route: string): void {
		const existingTab = this.findTabByRoute(route)
		if (existingTab) {
			// Switch to the existing tab (preserves query params)
			this.switchToTab(existingTab.id)
		} else {
			this.createTabFromRoute(route)
		}
	}

	/**
	 * Handle route changes - main entry point for global route handler
	 */
	handleRouteChange(to: string): void {
		// console.log('TabService: handleRouteChange called with', to)

		// Prevent infinite loops
		if (this.isUpdatingRoute) {
			// console.log('TabService: Skipping route change due to update in progress')
			return
		}

		// Don't create tabs for excluded routes
		if (this.routeMetadata.isExcludedRoute(to)) {
			// console.log('TabService: Route excluded, skipping', to)
			return
		}

		// Check if target route requires a tab
		if (!this.routeMetadata.requiresTab(to)) {
			// console.log('TabService: Route does not require tab', to)
			return
		}

		// Always update active tab's route with current URL (including query params)
		this.updateActiveTabRoute(to)

		// Sync or create tab for the new route
		this.syncTabWithRoute(to)
	}

	/**
	 * Update the active tab's route with new query parameters
	 */
	private updateActiveTabRoute(newRoute: string): void {
		// Prevent infinite loops
		if (this.isUpdatingRoute) {
			return
		}

		const activeTabId = this.activeTabIdSignal()
		if (!activeTabId) return

		const currentTabs = this.tabsSignal()
		const activeTab = currentTabs.find(tab => tab.id === activeTabId)

		if (activeTab) {
			// Check if the base route matches (ignoring query params)
			const currentBaseRoute = this.extractBaseRoute(activeTab.route)
			const newBaseRoute = this.extractBaseRoute(newRoute)

			// Always update if base routes match, regardless of query params
			if (currentBaseRoute === newBaseRoute && activeTab.route !== newRoute) {
				// Prevent recursive updates
				this.isUpdatingRoute = true

				// Update the active tab's route with new query parameters
				const updatedTabs = currentTabs.map(tab =>
					tab.id === activeTabId
						? { ...tab, route: newRoute }
						: tab
				)

				this.tabsSignal.set(updatedTabs)
				this.saveTabsToStorage()

				// console.log('TabService: Updated active tab route', {
				// 	tabId: activeTabId,
				// 	oldRoute: activeTab.route,
				// 	newRoute: newRoute
				// })

				// Reset the flag after a short delay
				setTimeout(() => {
					this.isUpdatingRoute = false
				}, 100)
			}
		}
	}

	/**
	 * Public method to update active tab's query parameters
	 * Call this when query parameters change while tab is active
	 */
	updateActiveTabQueryParams(newUrl: string): void {
		this.updateActiveTabRoute(newUrl)
	}

	/**
	 * Get appropriate title for a route (handles dynamic routes)
	 */
	private getTabTitleForRoute(route: string): string {
		// First try to get title from route metadata (which now includes menu titles)
		const routeTitle = this.routeMetadata.getTabTitle(route)
		if (routeTitle && routeTitle !== 'Unknown Page') {
			// console.log('TabService: Using route metadata title', { route, title: routeTitle })
			return routeTitle
		}

		// Generate title from route path for dynamic routes
		const generatedTitle = this.generateTitleFromRoute(route)
		// console.log('TabService: Using generated title', { route, title: generatedTitle })
		return generatedTitle
	}

	/**
	 * Generate a readable title from route path
	 */
	private generateTitleFromRoute(route: string): string {
		// Remove query parameters first
		const cleanRoute = this.extractBaseRoute(route)
		// console.log('TabService: generateTitleFromRoute - cleanRoute:', cleanRoute, 'from original route:', route)

		const segments = cleanRoute.split('/').filter(segment => segment)
		const lastSegment = segments[segments.length - 1]
		// console.log('TabService: generateTitleFromRoute - segments:', segments, 'lastSegment:', lastSegment)

		// Handle numeric IDs
		if (/^\d+$/.test(lastSegment)) {
			const parentSegment = segments[segments.length - 2]
			return `${this.capitalizeFirst(parentSegment)} #${lastSegment}`
		}

		// Handle empty or root routes - try to infer from query parameters
		if (!lastSegment || lastSegment === '') {
			// console.log('TabService: generateTitleFromRoute - root route detected, checking query params')

			// Try to infer page type from query parameters
			const queryParams = this.extractQueryParams(route)
			if (queryParams.includes('termId')) {
				return 'Term Management'
			}
			if (queryParams.includes('organId')) {
				return 'Organization'
			}
			if (queryParams.includes('userId')) {
				return 'User Management'
			}

			// console.log('TabService: generateTitleFromRoute - returning Dashboard for empty route')
			return 'Dashboard'
		}

		const title = this.capitalizeFirst(lastSegment)
		// console.log('TabService: generateTitleFromRoute - returning title:', title)
		return title
	}

	/**
	 * Capitalize first letter of a string
	 */
	private capitalizeFirst(str: string): string {
		return str.charAt(0).toUpperCase() + str.slice(1)
	}

	/**
	 * Initialize default tab without triggering navigation
	 */
	initializeDefaultTab(route: string, title: string): void {
		const newTab: Tab = {
			id: this.generateTabId(),
			title: title,
			route: route,
			isActive: true,
			createdAt: Date.now()
		}

		this.tabsSignal.set([newTab])
		this.activeTabIdSignal.set(newTab.id)
		this.saveTabsToStorage()
	}

	/**
	 * Get maximum tab limit
	 */
	getMaxTabs(): number {
		return this.maxTabs
	}

	/**
	 * Set maximum tab limit
	 */
	setMaxTabs(limit: number): void {
		this.maxTabs = Math.max(1, limit) // Ensure at least 1 tab
		localStorage.setItem('app-max-tabs', this.maxTabs.toString())
	}

	/**
	 * Show tab limit notification to user
	 */
	private showTabLimitNotification(): void {
		// This method is now only used for manual tab creation failures
		// Auto-close behavior is handled in createTabFromRoute and createTab methods
		const message = `Maximum tab limit (${this.maxTabs}) reached. Oldest tab will be closed automatically for new tabs.`
		console.warn(message)

		// You could dispatch an event for UI components to handle
		if ('EventSource' in window) {
			const event = new CustomEvent('tabLimitReached', {
				detail: {
					message,
					limit: this.maxTabs,
					currentCount: this.tabsSignal().length,
					autoCloseEnabled: true
				}
			})
			window.dispatchEvent(event)
		}
	}

	/**
	 * Close the oldest tab to make room for a new one
	 */
	closeOldestTab(): Tab | null {
		const currentTabs = this.tabsSignal()
		if (currentTabs.length === 0) return null

		// Find the oldest tab (excluding the active one if possible)
		const nonActiveTabs = currentTabs.filter(tab => !tab.isActive)
		const tabToClose = nonActiveTabs.length > 0
			? nonActiveTabs.reduce((oldest, tab) =>
				tab.createdAt < oldest.createdAt ? tab : oldest
			)
			: currentTabs.reduce((oldest, tab) =>
				tab.createdAt < oldest.createdAt ? tab : oldest
			)

		this.closeTab(tabToClose.id)
		return tabToClose
	}

	/**
	 * Create tab with replacement strategy
	 */
	createTabWithReplacement(route: string): Tab | null {
		if (!this.isTabLimitReached()) {
			return this.createTabFromRoute(route)
		}

		// Close oldest tab and create new one
		const closedTab = this.closeOldestTab()
		if (closedTab) {
			// console.log(`Closed oldest tab "${closedTab.title}" to make room for new tab`)
			return this.createTabFromRoute(route)
		}

		return null
	}

	/**
	 * Extract query parameters from a route
	 */
	private extractQueryParams(route: string): string {
		const queryIndex = route.indexOf('?')
		return queryIndex !== -1 ? route.substring(queryIndex) : ''
	}

	/**
	 * Extract base route without query parameters
	 */
	private extractBaseRoute(route: string): string {
		const queryIndex = route.indexOf('?')
		return queryIndex !== -1 ? route.substring(0, queryIndex) : route
	}

	/**
	 * Merge query parameters from current route into target route
	 */
	private mergeQueryParams(targetRoute: string, currentRoute: string): string {
		const targetBase = this.extractBaseRoute(targetRoute)
		const currentQuery = this.extractQueryParams(currentRoute)
		const targetQuery = this.extractQueryParams(targetRoute)

		// If target already has query params, keep them
		// Otherwise, use current query params
		return targetQuery ? targetRoute : `${targetBase}${currentQuery}`
	}

	/**
	 * Check if tab limit is reached
	 */
	isTabLimitReached(): boolean {
		return this.tabsSignal().length >= this.maxTabs
	}
}